import { useEffect, useMemo, useRef, useState } from "react";

import { useGetRecords } from "api";
import { useFiltersContext } from "components/contexts";
import { getSelectedPowerPlant, useAppSelector } from "store";
import { useToday } from "utils/hooks/useToday";

import {
  appendInverterTipRecord,
  appendLiveRecord,
  counterOf,
  createHelperFunctionsGrouping,
  cumulativeAtEnd,
  EMPTY_INVERTER_TIP,
  InverterTipState,
  latestRecord,
  nextInverterTip,
} from "../utils";

export const useRecordsData = (dashboard?: boolean) => {
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const liveDataDate = useMemo(
    () =>
      selectedPowerPlant?.liveData?.date
        ? new Date(selectedPowerPlant.liveData.date)
        : undefined,
    [selectedPowerPlant?.liveData?.date],
  );
  const {
    date,
    dateType: dateTypeContext,
    energyType: energyTypeContext,
    chartGranularityType,
  } = useFiltersContext();
  const today = useToday(selectedPowerPlant?.timezone || "Europe/Warsaw");

  const dateType = dateTypeContext[0];
  const energyType = energyTypeContext[0];

  const helperFunctions = useMemo(
    () => createHelperFunctionsGrouping(selectedPowerPlant, chartGranularityType[0]),
    [selectedPowerPlant, chartGranularityType],
  );

  const startDate = useMemo(
    () => helperFunctions[dateType].start(dashboard ? today : date[dateType]),
    [helperFunctions, dateType, dashboard, today, date],
  );
  
  const endDate = useMemo(
    () => helperFunctions[dateType].end(dashboard ? today : date[dateType]),
    [helperFunctions, dateType, dashboard, today, date],
  );

  const isTodaySelected = useMemo(
    () =>
      today.getTime() >= startDate.getTime() &&
      today.getTime() <= endDate.getTime(),
    [startDate, endDate, today],
  );

  const { data, isLoading, refetch } = useGetRecords({
    request: {
      startDate: startDate!.toISOString(),
      endDate: endDate!.toISOString(),
    },
    options: {
      refetchInterval: isTodaySelected
        ? (selectedPowerPlant?.recordSaveInterval ?? 5 * 60 * 1000)
        : false,
      refetchOnWindowFocus: isTodaySelected,
      staleTime: isTodaySelected
        ? selectedPowerPlant?.liveDataInterval
        : selectedPowerPlant?.recordSaveInterval,
      enabled: !!(startDate && endDate),
    },
  });

  // Latest stored record, found once and reused for the live-data gap start
  // and (for inverter linear) the open interval's base cumulative.
  const lastStored = useMemo(
    () => (data && data.length ? latestRecord(data) : undefined),
    [data],
  );

  // End timestamp of the last stored record = start of the live-data gap.
  const lastRecordEndMs = useMemo(
    () =>
      lastStored
        ? new Date(lastStored.date).getTime() +
          (lastStored.period || 5 * 60 * 1000)
        : 0,
    [lastStored],
  );

  // When live data has advanced past where a new stored record should already
  // exist (but hasn't been fetched yet), force an immediate refetch so the
  // synthetic doesn't overlap with a period that's already in the DB.
  const lastForceRefetchRef = useRef<number>(0);
  useEffect(() => {
    if (!liveDataDate || !isTodaySelected || !lastRecordEndMs) return;
    const recordSaveInterval = selectedPowerPlant?.recordSaveInterval || 5 * 60 * 1000;
    if (
      liveDataDate.getTime() > lastRecordEndMs + recordSaveInterval &&
      Date.now() - lastForceRefetchRef.current > (selectedPowerPlant?.liveDataInterval ?? 30_000)
    ) {
      lastForceRefetchRef.current = Date.now();
      refetch();
    }
  }, [liveDataDate, lastRecordEndMs, isTodaySelected, selectedPowerPlant?.recordSaveInterval, selectedPowerPlant?.liveDataInterval, refetch]);

  const liveData = selectedPowerPlant?.liveData;
  // The device's counter, which is what `openIntervalBaseTotal` below is
  // measured in. `total` is the plant's lifetime across every source it has
  // ever had, and subtracting one from the other gives a nonsense figure.
  const liveTotal = liveData?.energyProduced ? counterOf(liveData.energyProduced) : undefined;

  // The inverter linear chart gets a tick-gated running-average tip
  // (nextInverterTip) instead of the generic synthetic, which appendLiveRecord
  // excludes — see nextInverterTip for why.
  const isInverterLinear =
    liveData?.source === "inverter" &&
    dateType === "day" &&
    chartGranularityType[0] === "linear";

  // Cumulative energy at the start of the currently-open interval.
  const openIntervalBaseTotal = lastStored?.energyProduced
    ? cumulativeAtEnd(lastStored.energyProduced)
    : undefined;

  // State machine lives in a ref (so "hold between ticks" survives renders);
  // a state mirror triggers the redraw only when the tip actually changes.
  const tipRef = useRef<InverterTipState>(EMPTY_INVERTER_TIP);
  const [tip, setTip] = useState<InverterTipState>(EMPTY_INVERTER_TIP);
  useEffect(() => {
    if (!isInverterLinear) {
      if (tipRef.current !== EMPTY_INVERTER_TIP) {
        tipRef.current = EMPTY_INVERTER_TIP;
        setTip(EMPTY_INVERTER_TIP);
      }
      return;
    }
    const next = nextInverterTip(tipRef.current, {
      liveTotal,
      openIntervalBaseTotal,
      liveDataMs: liveDataDate?.getTime(),
      lastRecordEndMs,
    });
    if (next !== tipRef.current) {
      tipRef.current = next;
      setTip(next);
    }
  }, [
    isInverterLinear,
    liveTotal,
    openIntervalBaseTotal,
    liveDataDate,
    lastRecordEndMs,
  ]);
  const tipPower = tip.tipPower;

  // Inject a synthetic record so the chart reflects the most current reading:
  // a tick-gated running-average tip for inverter+linear, otherwise the
  // generic energy synthetic (meter linear, and all 15min/1hour buckets).
  const dataWithLive = useMemo(
    () =>
      isInverterLinear
        ? appendInverterTipRecord(data, {
            tipPower,
            lastRecordEndMs,
            periodMs: selectedPowerPlant?.recordSaveInterval || 5 * 60 * 1000,
            liveTotal,
            isTodaySelected,
          })
        : appendLiveRecord(data, {
            liveData,
            liveDataDate,
            lastRecordEndMs,
            isTodaySelected,
            dateType,
            chartGranularityType: chartGranularityType[0],
          }),
    [
      isInverterLinear,
      tipPower,
      data,
      isTodaySelected,
      lastRecordEndMs,
      selectedPowerPlant?.recordSaveInterval,
      liveTotal,
      liveData,
      liveDataDate,
      dateType,
      chartGranularityType,
    ],
  );

  const preparedData = useMemo(
    () =>
      dataWithLive
        ? helperFunctions[dateType].prepareData(dataWithLive, date[dateType], liveDataDate)
        : undefined,
    [dataWithLive, helperFunctions, date, dateType, liveDataDate],
  );

  return {
    data,
    isLoading,
    preparedData,
    helperFunctions,
    dateType,
    energyType,
    startDate,
    endDate,
    today,
    isTodaySelected,
  };
};